import { AuthProvider } from "../values";
import { microsoftAuthService } from "./config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export const fetchOneDriveFiles = async (account, setLoading, setError) => {
    try {
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }
        setLoading && setLoading(true);
        let allFiles = [];
        let nextLink = 'https://graph.microsoft.com/v1.0/me/drive/root/delta';

        const getAccessToken = async (interactive = false) => {
            try {
                if (typeof account === 'string') {
                    return await microsoftAuthService.getAccessToken(account, ['Files.Read'], interactive);
                } else if (account.id) {
                    return await microsoftAuthService.getAccessToken(account.id, ['Files.Read'], interactive);
                } else {
                    const tokenResponse = await (interactive ?
                        microsoftAuthService.msalInstance.acquireTokenPopup({
                            scopes: ['Files.Read'],
                            account: account,
                        }) :
                        microsoftAuthService.msalInstance.acquireTokenSilent({
                            scopes: ['Files.Read'],
                            account: account,
                        })
                    );
                    return tokenResponse.accessToken;
                }
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError && !interactive) {
                    return await getAccessToken(true);
                }
                throw error;
            }
        };
        while (nextLink) {
            try {
                const accessToken = await getAccessToken();
                const result = await fetch(nextLink, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                if (!result.ok) {
                    const errorData = await result.json().catch(() => ({}));
                    throw new Error(errorData.error ? errorData.error.message : `HTTP error ${result.status}`);
                }
                const data = await result.json();
                const filesOnly = data.value.filter(item => !item.folder);
                allFiles = allFiles.concat(filesOnly.map(file => ({
                    ...file,
                    account: account,
                    provider: AuthProvider.MICROSOFT,
                })));
                nextLink = data['@odata.nextLink'] || null;
            } catch (error) {
                if (error.message && (error.message.includes("token") ||
                    error.message.includes("auth") ||
                    error.message.includes("401"))) {
                    const accessToken = await getAccessToken(true);
                    const result = await fetch(nextLink, {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    });
                    if (!result.ok) {
                        const errorData = await result.json().catch(() => ({}));
                        throw new Error(errorData.error ? errorData.error.message : `HTTP error ${result.status}`);
                    }
                    const data = await result.json();
                    const filesOnly = data.value.filter(item => !item.folder);
                    allFiles = allFiles.concat(filesOnly.map(file => ({
                        ...file,
                        account: account,
                        provider: AuthProvider.MICROSOFT,
                    })));
                    nextLink = data['@odata.nextLink'] || null;
                } else {
                    throw error;
                }
            }
        }
        return allFiles;
    } catch (error) {
        setError && setError(`Failed to fetch OneDrive files: ${error.message}`);
        throw error;
    } finally {
        setLoading && setLoading(false);
    }
};

export const searchOneDriveFiles = async (query, setLoading, activeAccount, setFileList) => {
    try {
        setLoading(true);
        let token = null;
        try {
            if (typeof activeAccount === 'string') {
                const tokenResponse = await microsoftAuthService.getAccessToken({
                    activeAccount,
                    scopes: ["Files.Read.All"],
                    interactive: false,
                });
                token = tokenResponse.accessToken;
            } else if (activeAccount.id) {
                const accountId = activeAccount.id;
                const tokenResponse = await microsoftAuthService.getAccessToken({
                    accountId,
                    scopes: ["Files.Read.All"],
                    interactive: false,
                });
                token = tokenResponse.accessToken;
            }
        } catch (authError) {
            throw new Error("Authentication failed. Please try logging in again.");
        }
        const searchBody = {
            requests: [{
                entityTypes: ["driveItem"],
                query: { queryString: query },
                from: 0,
                size: 50,
                fields: ["name", "id", "size", "lastModifiedDateTime", "webUrl", "file", "folder"],
            }]
        };
        const response = await fetch('https://graph.microsoft.com/v1.0/search/query', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(searchBody),
        });
        if (!response.ok) {
            throw new Error(`Search failed with status: ${response.status}`);
        }
        const data = await response.json();
        const hitContainers = data.value[0]?.hitsContainers || [];
        const searchResults = [];
        for (const container of hitContainers) {
            if (container.hits && container.hits.length > 0) {
                for (const hit of container.hits) {
                    if (hit.resource) {
                        searchResults.push({
                            id: hit.resource.id,
                            name: hit.resource.name,
                            size: hit.resource.size,
                            modifiedTime: hit.resource.lastModifiedDateTime,
                            webViewLink: hit.resource.webUrl,
                            mimeType: hit.resource.file ? hit.resource.file.mimeType :
                                (hit.resource.folder ? 'folder' : 'unknown'),
                            provider: AuthProvider.MICROSOFT,
                        });
                    }
                }
            }
        }
        setFileList(searchResults);
    } catch (error) {
        throw error;
    } finally {
        setLoading(false);
    }
};

export const downloadOneDriveFile = async (fileId, account, setLoading, setError) => {
    try {
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }
        setLoading && setLoading(true);
        const getAccessToken = async (interactive = false) => {
            try {
                if (typeof account === 'string') {
                    return await microsoftAuthService.getAccessToken(account, ['Files.Read'], interactive);
                } else if (account.id) {
                    return await microsoftAuthService.getAccessToken(account.id, ['Files.Read'], interactive);
                } else {
                    const tokenResponse = await (interactive ?
                        microsoftAuthService.msalInstance.acquireTokenPopup({
                            scopes: ['Files.Read'],
                            account: account,
                        }) :
                        microsoftAuthService.msalInstance.acquireTokenSilent({
                            scopes: ['Files.Read'],
                            account: account,
                        })
                    );
                    return tokenResponse.accessToken;
                }
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError && !interactive) {
                    return await getAccessToken(true);
                }
                throw error;
            }
        };
        try {
            const accessToken = await getAccessToken();
            const result = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/content`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!result.ok) {
                const errorText = await result.text();
                let errorMessage = `HTTP error ${result.status}`;
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.error ? errorData.error.message : errorMessage;
                } catch (e) {
                    errorMessage = errorText || errorMessage;
                }
                throw new Error(errorMessage);
            }
            return await result.blob();
        } catch (error) {
            if (error.message && (error.message.includes("token") ||
                error.message.includes("auth") ||
                error.message.includes("401"))) {
                const accessToken = await getAccessToken(true);
                const result = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/content`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                if (!result.ok) {
                    const errorText = await result.text();
                    let errorMessage = `HTTP error ${result.status}`;
                    try {
                        const errorData = JSON.parse(errorText);
                        errorMessage = errorData.error ? errorData.error.message : errorMessage;
                    } catch (e) {
                        errorMessage = errorText || errorMessage;
                    }
                    throw new Error(errorMessage);
                }
                return await result.blob();
            } else {
                throw error;
            }
        }
    } catch (error) {
        setError && setError(`Failed to download file: ${error.message}`);
        throw error;
    } finally {
        setLoading && setLoading(false);
    }
};

export const createOneDriveFile = async (fileName, fileContent, mimeType, account, setLoading, setError) => {
    try {
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }
        setLoading && setLoading(true);
        const getAccessToken = async (interactive = false) => {
            try {
                if (typeof account === 'string') {
                    return await microsoftAuthService.getAccessToken(account, ['Files.ReadWrite'], interactive);
                } else if (account.id) {
                    return await microsoftAuthService.getAccessToken(account.id, ['Files.ReadWrite'], interactive);
                } else {
                    const tokenResponse = await (interactive ?
                        microsoftAuthService.msalInstance.acquireTokenPopup({
                            scopes: ['Files.ReadWrite'],
                            account: account,
                        }) :
                        microsoftAuthService.msalInstance.acquireTokenSilent({
                            scopes: ['Files.ReadWrite'],
                            account: account,
                        })
                    );
                    return tokenResponse.accessToken;
                }
            } catch (error) {
                if (error.name === "InteractionRequiredAuthError" && !interactive) {
                    return await getAccessToken(true);
                }
                throw error;
            }
        };
        try {
            const accessToken = await getAccessToken();
            if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                const apiUrl = `https://graph.microsoft.com/v1.0/me/drive/root:/${fileName}:/content`;
                let contentToUpload;
                if (fileContent instanceof Blob) {
                    contentToUpload = await fileContent.text();
                } else if (typeof fileContent === 'string') {
                    contentToUpload = fileContent;
                } else {
                    contentToUpload = 'Empty document';
                }
                if (contentToUpload.includes('<')) {
                    contentToUpload = contentToUpload.replace(/<[^>]*>/g, ' ');
                }
                const result = await fetch(apiUrl, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'text/plain',
                    },
                    body: contentToUpload,
                });
                if (!result.ok) {
                    const errorText = await result.text();
                    throw new Error(errorText || `HTTP error ${result.status}`);
                }
                const fileData = await result.json();
                return {
                    ...fileData,
                    account: account,
                    provider: AuthProvider.MICROSOFT,
                };
            } else if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
                const apiUrl = `https://graph.microsoft.com/v1.0/me/drive/root:/${fileName}:/content`;
                let fileBlob;
                if (fileContent instanceof ArrayBuffer) {
                    fileBlob = new Blob([new Uint8Array(fileContent)], { type: mimeType });
                } else if (typeof fileContent === 'string') {
                    fileBlob = new Blob([fileContent], { type: mimeType });
                } else {
                    fileBlob = fileContent;
                }
                const result = await fetch(apiUrl, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': mimeType,
                    },
                    body: fileBlob,
                });
                if (!result.ok) {
                    const errorText = await result.text();
                    throw new Error(errorText || `HTTP error ${result.status}`);
                }
                const fileData = await result.json();
                return {
                    ...fileData,
                    account: account,
                    provider: AuthProvider.MICROSOFT,
                };
            } else if (mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
                const apiUrl = `https://graph.microsoft.com/v1.0/me/drive/root:/${fileName}:/content`;
                let fileBlob;
                if (fileContent instanceof ArrayBuffer) {
                    fileBlob = new Blob([new Uint8Array(fileContent)], { type: mimeType });
                } else if (typeof fileContent === 'string') {
                    fileBlob = new Blob([fileContent], { type: mimeType });
                } else {
                    fileBlob = fileContent;
                }
                const result = await fetch(apiUrl, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': mimeType,
                    },
                    body: fileBlob,
                });
                if (!result.ok) {
                    const errorText = await result.text();
                    throw new Error(errorText || `HTTP error ${result.status}`);
                }
                const fileData = await result.json();
                return {
                    ...fileData,
                    account: account,
                    provider: AuthProvider.MICROSOFT,
                };
            } else {
                const createResponse = await fetch('https://graph.microsoft.com/v1.0/me/drive/root/children', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        "@microsoft.graph.conflictBehavior": "rename",
                        "name": fileName,
                        "file": {},
                    }),
                });
                if (!createResponse.ok) {
                    const errorText = await createResponse.text();
                    throw new Error(errorText || `HTTP error ${createResponse.status}`);
                }
                const fileData = await createResponse.json();
                const fileId = fileData.id;
                let fileBlob;
                if (fileContent instanceof ArrayBuffer) {
                    fileBlob = new Blob([new Uint8Array(fileContent)], { type: mimeType });
                } else if (typeof fileContent === 'string') {
                    fileBlob = new Blob([fileContent], { type: mimeType });
                } else {
                    fileBlob = fileContent;
                }
                const uploadResponse = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/content`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': mimeType,
                    },
                    body: fileBlob,
                });
                if (!uploadResponse.ok) {
                    const errorText = await uploadResponse.text();
                    throw new Error(errorText || `HTTP error ${uploadResponse.status}`);
                }
                const uploadedFileData = await uploadResponse.json();
                return {
                    ...uploadedFileData,
                    account: account,
                    provider: AuthProvider.MICROSOFT,
                };
            }
        } catch (error) {
            if (error.message && (error.message.includes("token") ||
                error.message.includes("auth") ||
                error.message.includes("401"))) {
                const newToken = await getAccessToken(true);
                if (!newToken) {
                    throw new Error("Failed to refresh authentication token");
                }
                return await createOneDriveFile(fileName, fileContent, mimeType, account, setLoading, setError);
            } else {
                throw error;
            }
        }
    } catch (error) {
        setError && setError(`Failed to create file: ${error.message}`);
        return null;
    } finally {
        setLoading && setLoading(false);
    }
};